import { useEffect, useState } from "react";
import { adminListSkills, adminCreateSkill, adminUpdateSkill, adminDeleteSkill } from "@/lib/api";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const RED = "#A32D2D";

/**
 * Admin management of the Available Today skills taxonomy.
 * Owners can add, deactivate, delete, or re-sort skills that appear on the
 * worker sign-up form and contractor filter pills.
 */
export default function AdminWorkforceSkillsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await adminListSkills();
      setItems(d.items || []);
    } catch (e) {
      setError(e?.message || "Failed to load.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e?.preventDefault();
    if (!newName.trim()) return;
    setBusy(true); setError(null);
    try {
      await adminCreateSkill(newName.trim(), (items.length || 0) + 1);
      setNewName("");
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not add.");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (skill) => {
    try {
      await adminUpdateSkill(skill.id, { active: !skill.active });
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Update failed.");
    }
  };

  const del = async (skill) => {
    if (!window.confirm(`Delete "${skill.name}"? Existing workers keep the label; the pill just disappears from filters.`)) return;
    try {
      await adminDeleteSkill(skill.id);
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Delete failed.");
    }
  };

  const move = async (skill, delta) => {
    const target = (skill.sort_order ?? 999) + delta;
    try {
      await adminUpdateSkill(skill.id, { sort_order: target });
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Reorder failed.");
    }
  };

  return (
    <div style={pageWrap} data-testid="admin-workforce-skills-page">
      <div style={{ maxWidth: 880, margin: "0 auto" }}>
        <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>ADMIN · AVAILABLE TODAY</div>
        <h1 style={h1}>WORKFORCE SKILLS TAXONOMY</h1>
        <p style={{ color: "#5b5e69", marginBottom: 24 }}>
          These are the skill pills workers pick from when they join and contractors pick from when they filter. Reorder with ↑ / ↓, toggle to hide from active use, or delete to remove entirely.
        </p>

        <form onSubmit={add} style={addCard}>
          <input value={newName} onChange={(e) => setNewName(e.target.value)}
                 placeholder="Add a new skill (e.g. Solar Installer)"
                 data-testid="new-skill-input"
                 style={{ ...inputStyle, flex: "1 1 260px" }} />
          <button type="submit" disabled={busy || !newName.trim()} data-testid="new-skill-add"
                  style={{ ...primaryBtn, opacity: busy ? 0.6 : 1 }}>
            {busy ? "Adding…" : "+ ADD SKILL"}
          </button>
        </form>

        {error && <div style={errBox} data-testid="skills-error">{error}</div>}

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#5b5e69" }}>Loading…</div>
        ) : (
          <div style={listCard} data-testid="skills-list">
            {items.map((s) => (
              <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid #f0f2f5" }} data-testid={`skill-row-${s.slug}`}>
                <button onClick={() => move(s, -1)} style={arrowBtn} data-testid={`skill-up-${s.slug}`} title="Move up">↑</button>
                <button onClick={() => move(s, 1)} style={arrowBtn} data-testid={`skill-down-${s.slug}`} title="Move down">↓</button>
                <div style={{ flex: 1, fontWeight: 700, color: s.active ? NAVY : "#9ca3af", textDecoration: s.active ? "none" : "line-through" }}>
                  {s.name}
                </div>
                <span style={{ fontSize: 10, letterSpacing: 1, color: "#9ca3af" }}>{s.slug}</span>
                <button onClick={() => toggleActive(s)} data-testid={`skill-toggle-${s.slug}`}
                        style={{ ...ghostBtn, background: s.active ? "#EFF1F5" : GOLD, color: s.active ? NAVY : NAVY }}>
                  {s.active ? "ACTIVE" : "INACTIVE"}
                </button>
                <button onClick={() => del(s)} data-testid={`skill-delete-${s.slug}`} style={{ ...ghostBtn, background: "#FDECEC", color: RED }}>
                  DELETE
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const pageWrap = { background: "#F8F9FA", minHeight: "100vh", padding: "40px 20px" };
const h1 = { fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2.5, fontSize: 42, color: NAVY, margin: "6px 0 12px" };
const addCard = { background: "#fff", padding: "16px 18px", borderRadius: 12, border: "1px solid #e5e7ee", display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 };
const listCard = { background: "#fff", padding: "10px 18px", borderRadius: 12, border: "1px solid #e5e7ee" };
const inputStyle = { padding: "12px 14px", border: "1px solid #d0d3da", borderRadius: 10, fontSize: 14, outline: "none" };
const primaryBtn = { background: NAVY, color: GOLD, border: "none", padding: "12px 20px", borderRadius: 10, fontWeight: 700, letterSpacing: 1.5, cursor: "pointer", fontSize: 13, fontFamily: "Bebas Neue, Arial, sans-serif" };
const ghostBtn = { background: "#EFF1F5", color: NAVY, border: "none", padding: "6px 12px", borderRadius: 8, fontSize: 11, fontWeight: 700, letterSpacing: 1, cursor: "pointer" };
const arrowBtn = { background: "transparent", border: "1px solid #d0d3da", borderRadius: 6, width: 28, height: 28, cursor: "pointer", color: NAVY, fontWeight: 700 };
const errBox = { color: RED, background: "#FDECEC", padding: "10px 14px", borderRadius: 10, marginBottom: 14, fontSize: 14 };
